import { Link, useLocation, useParams } from 'react-router-dom';
import { BackControl } from '../components/BackControl.tsx';
import { useClientBanner, useClientTimeline, useMe } from '../api/queries.ts';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import { ClientHeader, splitByBand } from '../components/ClientHeader.tsx';
import { CriticalFlags } from '../components/FlagBanner.tsx';
import { PlacementTimeline } from '../components/PlacementTimeline.tsx';
import { CourtOrderPanel } from '../components/CourtOrderPanel.tsx';
import { CurrentStanding } from '../components/CurrentStanding.tsx';
import { CaseTeamPanel } from '../components/CaseTeamPanel.tsx';
import { WhereaboutsPanel } from '../components/WhereaboutsPanel.tsx';
import { VisitStandardPanel } from '../components/VisitStandardPanel.tsx';
import { AdvancedPanel } from '../components/AdvancedPanel.tsx';
import { Staleness } from '../components/Staleness.tsx';
import { ApiError } from '../api/client.ts';

export function ClientDashboard() {
  const { id = '' } = useParams();
  // What the visit form left behind on its way out. It replaces its own history
  // entry, so by the time this renders the form is gone and this record is the
  // only place left to say the visit was saved.
  const { state } = useLocation();
  const recorded = (state as { recorded?: string } | null)?.recorded ?? null;
  const banner = useClientBanner(id);
  const history = useClientTimeline(id);
  // Asked so a 403 can be told apart. This client's routes answer 403 both
  // for "outside your caseload" and, from the authenticator, for "no staff
  // account is linked to this sign-in". Only /me tells which: it has no
  // per-client check, so a 403 there is always the account.
  const me = useMe();

  const sessionError = [me.error, banner.error, history.error].find(
    (e) => e !== null && isSessionFailure(e, { forbiddenIsSession: e === me.error }),
  );
  if (sessionError) {
    return (
      <div className="mx-auto max-w-3xl">
        <LoadFailure what="This client" error={sessionError} onRetry={() => void banner.refetch()} />
      </div>
    );
  }

  // isPending, not isLoading. Between retries isLoading goes false while the
  // query is still pending and has no data, so branching on it blanks the
  // screen during the backoff gaps — exactly when the connection is worst and
  // the user most needs to be told something is happening.
  if (banner.isPending) {
    return <p className="p-6 text-neutral-500">Loading client…</p>;
  }

  /**
   * An error only takes over the screen when there is nothing to show.
   *
   * TanStack keeps the last good answer through a failed refetch: the query
   * goes to `status: 'error'` while `data` stays exactly as it was. Branching
   * on `banner.error` alone therefore threw away a client record — Critical
   * flags included — the moment one refetch failed on bad wifi, and replaced
   * it with "Something went wrong". It also made `Staleness`'s own error
   * branch unreachable, so the component written to handle precisely this
   * could never run.
   *
   * With data in hand the failure is reported by `Staleness` instead: the
   * screen keeps the last good rows and says how old they are. Whereabouts
   * from ten minutes ago is worth something; a blank page is worth nothing,
   * and a blank page in a lobby is worse than either.
   */
  if (banner.error && !banner.data) {
    const err = banner.error;
    const status = err instanceof ApiError ? err.status : 0;
    // Not "Not your caseload" until /me has said the account itself is fine.
    if (status === 403 && me.isPending) {
      return <p className="p-6 text-neutral-500">Loading client…</p>;
    }
    if (status !== 403 && status !== 404) {
      return (
        <div className="mx-auto max-w-3xl">
          <LoadFailure
            what="This client"
            error={err}
            onRetry={() => void banner.refetch()}
            retrying={banner.isFetching}
            forbiddenIsSession={false}
          />
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-lg font-bold text-neutral-900">
          {status === 403 ? 'Not your caseload' : 'No such client'}
        </h1>
        <p className="mt-2 text-neutral-700">{err.message}</p>
        {err instanceof ApiError && err.hint && (
          <p className="mt-1 text-neutral-500">{err.hint}</p>
        )}
        <button
          type="button"
          onClick={() => void banner.refetch()}
          className="mt-5 min-h-[48px] rounded-lg bg-neutral-900 px-5 font-semibold text-white
                     active:bg-neutral-700"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!banner.data) return null;

  const { critical } = splitByBand(banner.data);

  return (
    <div className="min-h-dvh bg-neutral-50">
      {/*
        Full-bleed, above everything, at every width.
        Not inside the header and not inside a column — on a wide screen the
        header sits beside the timeline, and a Critical band the width of one
        column is exactly the weakening this rule exists to prevent.
      */}
      <div className="sticky top-0 z-20">
        <CriticalFlags flags={critical} />
      </div>

      <div className="mx-auto max-w-[96rem] px-0 lg:px-6 lg:py-6">
        <nav className="px-4 pt-3 lg:px-0 lg:pt-0 lg:pb-3">
          <BackControl />
        </nav>

        {/* A confirmation, not a warning: `note`, not `warning` or `critical`. */}
        {recorded && (
          <p
            role="status"
            className="mx-4 mb-3 rounded-lg border-l-8 border-note-edge bg-note-bg px-4 py-3
                       font-semibold text-note-fg lg:mx-0"
          >
            {recorded}
          </p>
        )}

        {/*
          One column on a tablet, two from `lg`. The header is a panel of facts
          about a person and stops being easier to read past about 30rem; the
          timeline is a list that benefits from every pixel it is given. Putting
          them side by side is what stops a 1920px screen being a 768px column
          with 576px of nothing on either side.
        */}
        <div className="lg:grid lg:grid-cols-[minmax(0,30rem)_minmax(0,1fr)] lg:items-start lg:gap-6">
          <div className="lg:sticky lg:top-6">
            <ClientHeader banner={banner.data} />

            <div className="bg-white lg:mt-3 lg:rounded-lg lg:shadow">
              <Staleness
                updatedAt={banner.dataUpdatedAt}
                isFetching={banner.isFetching}
                error={banner.error}
                onRefresh={() => { void banner.refetch(); void history.refetch(); }}
              />

              {/* The one thing this screen is for, other than reading it. A case
                  manager who can look up a client and record a visit has a tool. */}
              <div className="px-4 pt-1 pb-3">
                <Link
                  to={`/clients/${id}/visit`}
                  className="flex min-h-[52px] w-full items-center justify-center rounded-lg
                             bg-neutral-900 px-4 font-semibold text-white
                             hover:bg-neutral-800 active:bg-neutral-700"
                >
                  Record a visit
                </Link>
              </div>

              <CourtOrderPanel clientId={id} />
            </div>
          </div>

          {/*
            Current facts first, then history, and never interleaved. Here in
            the wide column rather than under the header: the left column is
            sticky, and a sticky column taller than the screen hides its own
            bottom.
          */}
          <div className="mt-3 space-y-3 lg:mt-0">
            <CurrentStanding
              clientId={id}
              legalStatus={banner.data.legal_status}
              entries={history.data?.entries}
            />

            <WhereaboutsPanel clientId={id} />

            <VisitStandardPanel clientId={id} />

            <CaseTeamPanel clientId={id} />

            <section className="bg-white lg:rounded-lg lg:shadow">
              <h2 className="px-4 pt-3 pb-1 text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
                History · placements, stays and visits
              </h2>
              {/* "No placement history recorded" only from an answer, never from a failure. */}
              {history.data ? (
                <>
                  <PlacementTimeline entries={history.data.entries} />
                  {/* The request asks for 100 and the OLDEST entries fall off,
                      because the sort is newest-first. A client hospitalised
                      often enough loses the start of their own history from
                      this screen with nothing to say so. `useMyWork` already
                      reports what it is not showing; this now does too. */}
                  {history.data.total > history.data.entries.length && (
                    <p className="px-4 pb-3 text-sm text-neutral-500">
                      Showing the most recent {history.data.entries.length} of{' '}
                      {history.data.total} entries. The earliest are not on this page.
                    </p>
                  )}
                </>
              ) : history.error ? (
                <LoadFailure
                  what="Placements and stays"
                  error={history.error}
                  onRetry={() => void history.refetch()}
                  retrying={history.isFetching}
                  forbiddenIsSession={false}
                />
              ) : (
                <p className="px-4 py-6 text-neutral-500">Loading history…</p>
              )}
            </section>

            {/* Last, and collapsed. Ending a case is irreversible and starts
                statutory clocks; it does not belong a misclick away from
                Record a visit. */}
            <AdvancedPanel clientId={id} />
          </div>
        </div>
      </div>
    </div>
  );
}
